import { usersGateway } from "@/gateways/usersGateway";
import { useLogout } from "@/login/useLogout";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import type { User } from "./types";

export function useGetUsers() {
  const query = useQuery({
    queryKey: ["users"],
    queryFn: () => usersGateway.listUsers(),
  });

  return { ...query, getUsersStatus: query.status, users: query.data };
}

export function useGetUser(id: string) {
  const query = useQuery({
    queryKey: ["user", id],
    queryFn: () => usersGateway.getUser(id),
  });

  return { ...query, getUserStatus: query.status, user: query.data };
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (user: User) => usersGateway.updateUser(user),
    onSuccess: (user: User) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.setQueryData(["user", user.id], user);
    },
  });

  return {
    ...mutation,
    updateUserStatus: mutation.status,
    updateUser: mutation.mutate,
  };
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (user: User) => usersGateway.deleteUser(user),
    onSuccess: (_, user: User) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user", user.id] });
    },
  });

  return {
    ...mutation,
    resetDeleteUser: mutation.reset,
    deleteUserStatus: mutation.status,
    deleteUser: mutation.mutate,
  };
}

export function useDeleteSelf() {
  const { logout } = useLogout();
  const mutation = useMutation({
    mutationFn: () => usersGateway.deleteSelf(),
    onSuccess: () => {
      logout();
    },
  });

  return {
    ...mutation,
    resetDeleteSelf: mutation.reset,
    deleteSelfStatus: mutation.status,
    deleteSelf: mutation.mutate,
  };
}
